import React from "react";
import { Bar, Line } from "react-chartjs-2";
import { Link } from "react-router-dom";
import {
  Activity,
  Clock3,
  Database,
  Download,
  ExternalLink,
  Globe2,
  Heart,
  Laptop,
  Mail,
  MapPinned,
  Megaphone,
  MousePointerClick,
  Radar,
  RotateCcw,
  Route,
  Search,
  TrendingUp,
  Users,
} from "lucide-react";
import { getCountryLabel } from "../utils/countryNames";

const formatDateTime = (value) =>
  value ? new Date(value).toLocaleString() : "Unknown";

const formatDuration = (value, zeroLabel = "0m") => {
  const totalSeconds = Math.max(0, Math.round((Number(value) || 0) / 1000));
  if (!totalSeconds) return zeroLabel;
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  if (minutes < 60) return `${minutes}m ${totalSeconds % 60}s`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
};

const formatPagePath = (pageUrl) => {
  if (!pageUrl) return "/";
  try {
    const parsed = new URL(pageUrl, "https://www.mohamedgad.com");
    return `${parsed.pathname}${parsed.search}`;
  } catch (error) {
    return pageUrl;
  }
};

const truncate = (value, maxLength = 48) =>
  `${value || ""}`.length > maxLength
    ? `${`${value || ""}`.slice(0, maxLength - 1)}…`
    : value || "";

const getAcquisition = (visitor) =>
  visitor?.latestAcquisition || visitor?.firstAcquisition || {};

const acquisitionLabel = (value) =>
  !value || value === "Unattributed" ? "Legacy / unattributed" : value;

const ReportButton = ({ onClick, disabled = false, busy = false, label }) => (
  <button
    type="button"
    className="card-report-button"
    onClick={onClick}
    disabled={disabled || busy}
    title={label || "Download report"}
  >
    <Download size={14} aria-hidden="true" />
    <span>{busy ? "Preparing" : "Download"}</span>
  </button>
);

const RankingList = ({ items, valueKey, labelFormatter = (value) => value }) => {
  if (!items.length) {
    return (
      <div className="analytics-empty-inline">
        New acquisition data will appear after the updated tracker is deployed.
      </div>
    );
  }

  const maximum = Math.max(1, ...items.map((item) => item.visitors || 0));

  return (
    <ol className="analytics-ranking-list">
      {items.map((item, index) => (
        <li key={`${item[valueKey]}-${index}`}>
          <span className="analytics-rank">{String(index + 1).padStart(2, "0")}</span>
          <div className="analytics-ranked-value">
            <div>
              <strong>{labelFormatter(item[valueKey])}</strong>
              <span>{(item.visits || item.visitors || 0).toLocaleString()} visits</span>
            </div>
            <div className="analytics-progress" aria-hidden="true">
              <span
                style={{
                  width: `${Math.max(
                    5,
                    Math.round(((item.visitors || 0) / maximum) * 100),
                  )}%`,
                }}
              />
            </div>
          </div>
          <strong className="analytics-ranked-count">
            {(item.visitors || 0).toLocaleString()}
          </strong>
        </li>
      ))}
    </ol>
  );
};

const WebsiteAnalyticsDashboard = ({
  analytics,
  engagementAnalytics,
  contacts,
  articleCount,
  loading,
  error,
  downloadError,
  trafficRange,
  trafficRangeOptions,
  selectedTrafficRange,
  setTrafficRange,
  trafficChartData,
  trafficChartOptions,
  pageChartData,
  pageChartOptions,
  engagementChartData,
  engagementChartOptions,
  visitorState,
  reports,
}) => {
  const summary = analytics?.summary || {};
  const engagementSummary = engagementAnalytics?.summary || {};
  const topSources = analytics?.topSources || [];
  const topChannels = analytics?.topChannels || [];
  const topCampaigns = analytics?.topCampaigns || [];
  const topLandingPages = analytics?.topLandingPages || [];
  const topCountries = analytics?.topCountries || [];
  const topCities = analytics?.topCities || [];
  const topReferrers = analytics?.topReferrers || [];
  const deviceBreakdown = analytics?.deviceBreakdown || [];
  const browserBreakdown = analytics?.browserBreakdown || [];
  const popularPages = analytics?.popularPages || [];
  const topArticles = engagementAnalytics?.topArticles || [];
  const recentComments = engagementAnalytics?.recentComments || [];
  const recentMessages = contacts.slice(0, 4);
  const returningRate = Math.round((summary.returningVisitorRate || 0) * 100);
  const engagementRate = Math.round((summary.engagementRate || 0) * 100);
  const contactRate = summary.totalUniqueVisitors
    ? ((contacts.length / summary.totalUniqueVisitors) * 100).toFixed(1)
    : "0.0";
  const metricCards = [
    {
      label: "Unique visitors",
      value: summary.totalUniqueVisitors || 0,
      helper: `${summary.activeVisitorsLast7Days || 0} active this week`,
      icon: Users,
      tone: "sky",
    },
    {
      label: "Visits",
      value: summary.totalVisits || 0,
      helper: `${summary.activeVisitorsLast30Days || 0} active in 30 days`,
      icon: Activity,
      tone: "cyan",
    },
    {
      label: "Page views",
      value: summary.totalPageViews || 0,
      helper: `${Number(summary.pagesPerVisit || 0).toFixed(1)} pages per visit`,
      icon: MousePointerClick,
      tone: "amber",
    },
    {
      label: "Engaged visitors",
      value: `${engagementRate}%`,
      helper: "30 seconds or more",
      icon: TrendingUp,
      tone: "slate",
    },
    {
      label: "Returning visitors",
      value: `${returningRate}%`,
      helper: `${summary.returningVisitors || 0} people came back`,
      icon: RotateCcw,
      tone: "rose",
    },
    {
      label: "Average visit",
      value: formatDuration(summary.averageEngagementMsPerVisit),
      helper: "Active time per visit",
      icon: Clock3,
      tone: "mint",
    },
  ];

  if (loading) {
    return (
      <section className="container pb-5">
        <div className="dashboard-loading-shell">
          <div className="dashboard-loading-card" />
          <div className="dashboard-loading-card" />
          <div className="dashboard-loading-card" />
        </div>
      </section>
    );
  }

  return (
    <>
      {error ? (
        <section className="container pb-3">
          <div className="alert alert-warning mb-0">{error}</div>
        </section>
      ) : null}
      {downloadError ? (
        <section className="container pb-3">
          <div className="alert alert-danger mb-0">{downloadError}</div>
        </section>
      ) : null}

      <section className="container pb-4">
        <div className="analytics-narrative">
          <div>
            <span className="analytics-live-dot" />
            <span>Measurement overview</span>
          </div>
          <p>
            <strong>{topSources[0] ? acquisitionLabel(topSources[0].source) : "Direct"}</strong>{" "}
            is the leading known source. {returningRate}% of visitors return and {engagementRate}%
            stay active for at least 30 seconds.
          </p>
          <span>
            Updated {formatDateTime(summary.lastUpdatedAt)}
          </span>
        </div>
      </section>

      <section className="container pb-4">
        <div className="dashboard-metric-grid">
          {metricCards.map(({ label, value, helper, icon: Icon, tone }) => (
            <div key={label} className={`dashboard-metric-card tone-${tone}`}>
              <div className="metric-card-top">
                <div className="metric-icon-wrap"><Icon size={18} /></div>
                <button
                  type="button"
                  className="metric-report-button"
                  onClick={() => reports.metric({ label, value, helper })}
                  aria-label={`Download ${label} report`}
                >
                  <Download size={14} />
                </button>
              </div>
              <div className="metric-value">
                {typeof value === "number" ? value.toLocaleString() : value}
              </div>
              <div className="metric-label">{label}</div>
              <div className="metric-helper">{helper}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="container pb-4">
        <div className="dashboard-card analytics-traffic-card">
          <div className="card-head">
            <div>
              <p className="dashboard-eyebrow mb-1">Audience movement</p>
              <h2 className="h4 fw-bold mb-1">Traffic over time</h2>
              <p className="text-muted mb-0">
                Daily visitors and page views. Private admin routes are excluded.
              </p>
            </div>
            <div className="card-head-actions">
              <ReportButton onClick={reports.traffic} disabled={!analytics} />
              <div className="stat-pill">
                <Radar size={16} /> {selectedTrafficRange.heading}
              </div>
              <div className="traffic-range-controls" role="group" aria-label="Traffic range">
                {trafficRangeOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={`traffic-range-button${trafficRange === option.value ? " active" : ""}`}
                    onClick={() => setTrafficRange(option.value)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="chart-shell chart-shell-lg">
            <Line data={trafficChartData} options={trafficChartOptions} />
          </div>
        </div>
      </section>

      <section className="container pb-4">
        <div className="analytics-section-heading">
          <div>
            <p className="dashboard-eyebrow mb-1">Acquisition</p>
            <h2>Where people found you</h2>
          </div>
          <p>First-touch source, channel, campaign, referring origin, and landing page.</p>
        </div>
        <div className="analytics-acquisition-grid">
          <div className="dashboard-card analytics-source-card">
            <div className="card-head compact">
              <div>
                <h3 className="h5 fw-bold mb-1">Top sources</h3>
                <p className="text-muted mb-0">Unique visitors by first known origin</p>
              </div>
              <ReportButton onClick={reports.acquisition} disabled={!analytics} />
            </div>
            <RankingList
              items={topSources}
              valueKey="source"
              labelFormatter={acquisitionLabel}
            />
          </div>

          <div className="dashboard-stack">
            <div className="dashboard-card analytics-channel-card">
              <div className="card-head compact">
                <div>
                  <h3 className="h5 fw-bold mb-1">Channel mix</h3>
                  <p className="text-muted mb-0">Search, social, paid, referral, and direct</p>
                </div>
                <Megaphone size={20} aria-hidden="true" />
              </div>
              <div className="analytics-channel-grid">
                {topChannels.length ? topChannels.slice(0, 6).map((item) => (
                  <div key={item.channel}>
                    <span>{acquisitionLabel(item.channel)}</span>
                    <strong>{(item.visitors || 0).toLocaleString()}</strong>
                    <small>{(item.visits || 0).toLocaleString()} visits</small>
                  </div>
                )) : <div className="analytics-empty-inline">Channel data starts with new visits.</div>}
              </div>
            </div>

            <div className="dashboard-card analytics-campaign-card">
              <div className="card-head compact">
                <div>
                  <h3 className="h5 fw-bold mb-1">Campaigns & referrers</h3>
                  <p className="text-muted mb-0">UTM campaigns and external domains</p>
                </div>
                <ExternalLink size={19} aria-hidden="true" />
              </div>
              <div className="analytics-dual-list">
                <div>
                  <span className="mini-section-label">Campaigns</span>
                  {topCampaigns.length ? (
                    <ul className="signal-list">
                      {topCampaigns.slice(0, 4).map((item) => (
                        <li key={item.campaign}><span>{item.campaign}</span><strong>{item.visitors}</strong></li>
                      ))}
                    </ul>
                  ) : <p className="analytics-empty-copy">No tagged campaigns yet.</p>}
                </div>
                <div>
                  <span className="mini-section-label">Referring origins</span>
                  {topReferrers.length ? (
                    <ul className="signal-list signal-list-break">
                      {topReferrers.slice(0, 4).map((item) => (
                        <li key={item.referrer}><span>{item.referrer}</span><strong>{item.visitors}</strong></li>
                      ))}
                    </ul>
                  ) : <p className="analytics-empty-copy">No external referrers captured yet.</p>}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="container pb-4">
        <div className="analytics-section-heading">
          <div>
            <p className="dashboard-eyebrow mb-1">Audience</p>
            <h2>Who is visiting</h2>
          </div>
          <p>Approximate geography and device context without browser-storage inspection.</p>
        </div>
        <div className="dashboard-grid dashboard-grid-secondary analytics-audience-grid">
          <div className="dashboard-card">
            <div className="card-head compact">
              <div>
                <h3 className="h5 fw-bold mb-1">Geographic reach</h3>
                <p className="text-muted mb-0">
                  {Math.round((summary.cityCoverageRate || 0) * 100)}% city coverage
                </p>
              </div>
              <Globe2 size={20} aria-hidden="true" />
            </div>
            <div className="audience-columns">
              <div>
                <div className="mini-section-label"><MapPinned size={14} /> Countries</div>
                {topCountries.length ? (
                  <ul className="signal-list">
                    {topCountries.slice(0, 6).map((item) => (
                      <li key={item.country}><span>{getCountryLabel(item.country)}</span><strong>{item.visitors}</strong></li>
                    ))}
                  </ul>
                ) : <p className="analytics-empty-copy">No country data yet.</p>}
              </div>
              <div>
                <div className="mini-section-label"><Route size={14} /> Cities</div>
                {topCities.length ? (
                  <ul className="signal-list signal-list-break">
                    {topCities.slice(0, 6).map((item) => (
                      <li key={`${item.city}-${item.region}`}>
                        <span>{item.city}<small>{item.region}, {getCountryLabel(item.country)}</small></span>
                        <strong>{item.visitors}</strong>
                      </li>
                    ))}
                  </ul>
                ) : <p className="analytics-empty-copy">No city data yet.</p>}
              </div>
            </div>
          </div>

          <div className="dashboard-card">
            <div className="card-head compact">
              <div>
                <h3 className="h5 fw-bold mb-1">Technology mix</h3>
                <p className="text-muted mb-0">Device category and browser</p>
              </div>
              <Laptop size={20} aria-hidden="true" />
            </div>
            <div className="analytics-dual-list">
              <div>
                <span className="mini-section-label">Devices</span>
                <ul className="signal-list">
                  {deviceBreakdown.slice(0, 5).map((item) => (
                    <li key={item.device}><span>{item.device}</span><strong>{item.visitors}</strong></li>
                  ))}
                </ul>
              </div>
              <div>
                <span className="mini-section-label">Browsers</span>
                <ul className="signal-list">
                  {browserBreakdown.slice(0, 5).map((item) => (
                    <li key={item.browser}><span>{item.browser}</span><strong>{item.visitors}</strong></li>
                  ))}
                </ul>
              </div>
            </div>
            {!deviceBreakdown.length ? (
              <div className="analytics-empty-inline mt-3">Technology data starts with the updated tracker.</div>
            ) : null}
          </div>
        </div>
      </section>

      <section className="container pb-4">
        <div className="analytics-section-heading">
          <div>
            <p className="dashboard-eyebrow mb-1">Content journey</p>
            <h2>What visitors consume</h2>
          </div>
          <p>Entry pages show what earns the click; page views show what holds attention.</p>
        </div>
        <div className="dashboard-grid dashboard-grid-secondary">
          <div className="dashboard-card analytics-landing-card">
            <div className="card-head compact">
              <div>
                <h3 className="h5 fw-bold mb-1">Top landing pages</h3>
                <p className="text-muted mb-0">The first page in each visitor journey</p>
              </div>
              <Route size={20} aria-hidden="true" />
            </div>
            <RankingList
              items={topLandingPages}
              valueKey="landingPage"
              labelFormatter={(value) => truncate(formatPagePath(value), 56)}
            />
          </div>

          <div className="dashboard-card chart-card">
            <div className="card-head compact">
              <div>
                <h3 className="h5 fw-bold mb-1">Most viewed pages</h3>
                <p className="text-muted mb-0">Page views during the last 30 days</p>
              </div>
              <ReportButton onClick={reports.pages} disabled={!popularPages.length} />
            </div>
            <div className="chart-shell chart-shell-md">
              {popularPages.length ? (
                <Bar data={pageChartData} options={pageChartOptions} />
              ) : <div className="chart-empty-state">Page performance will appear as traffic arrives.</div>}
            </div>
          </div>
        </div>
      </section>

      <section className="container pb-4">
        <div className="analytics-section-heading">
          <div>
            <p className="dashboard-eyebrow mb-1">Outcomes</p>
            <h2>What the traffic produced</h2>
          </div>
          <p>Contact intent and article interactions are kept downstream from traffic metrics.</p>
        </div>
        <div className="dashboard-grid dashboard-grid-secondary">
          <div className="dashboard-card chart-card">
            <div className="card-head compact">
              <div>
                <h3 className="h5 fw-bold mb-1">Article interaction</h3>
                <p className="text-muted mb-0">Likes, comments, and shares by article</p>
              </div>
              <ReportButton onClick={reports.articleEngagement} disabled={!topArticles.length} />
            </div>
            <div className="article-summary-strip">
              <span>{articleCount} published</span>
              <span>{engagementSummary.totalLikes || 0} likes</span>
              <span>{engagementSummary.totalComments || 0} comments</span>
              <span>{engagementSummary.totalShares || 0} shares</span>
            </div>
            <div className="chart-shell chart-shell-md">
              {topArticles.length ? (
                <Bar data={engagementChartData} options={engagementChartOptions} />
              ) : <div className="chart-empty-state">Article interactions will appear here.</div>}
            </div>
          </div>

          <div className="dashboard-card analytics-outcome-card">
            <div className="card-head compact">
              <div>
                <h3 className="h5 fw-bold mb-1">Intent signals</h3>
                <p className="text-muted mb-0">Actions that go beyond reading</p>
              </div>
              <ReportButton onClick={reports.leadFlow} disabled={!contacts.length} />
            </div>
            <div className="analytics-outcome-grid">
              <div><Mail size={18} /><strong>{contacts.length}</strong><span>contact messages</span></div>
              <div><Heart size={18} /><strong>{engagementSummary.totalEngagements || 0}</strong><span>article interactions</span></div>
              <div><TrendingUp size={18} /><strong>{contactRate}%</strong><span>visitor-to-contact rate</span></div>
            </div>
            <div className="analytics-recent-signals">
              <div>
                <span className="mini-section-label">Latest messages</span>
                {recentMessages.length ? recentMessages.map((message) => (
                  <p key={message._id}><strong>{message.subject || "General inquiry"}</strong><span>{message.name} · {formatDateTime(message.createdAt || message.timestamp)}</span></p>
                )) : <p className="analytics-empty-copy">No messages yet.</p>}
              </div>
              <div>
                <span className="mini-section-label">Latest comments</span>
                {recentComments.length ? recentComments.slice(0, 4).map((comment) => (
                  <p key={comment.id}><Link to={`/articles/${comment.slug}`}>{comment.articleTitle || comment.slug}</Link><span>{comment.name} · {formatDateTime(comment.createdAt)}</span></p>
                )) : <p className="analytics-empty-copy">No comments yet.</p>}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="container pb-5">
        <div className="dashboard-card visitor-table-card analytics-journey-card">
          <div className="card-head">
            <div>
              <p className="dashboard-eyebrow mb-1">Visitor journeys</p>
              <h2 className="h4 fw-bold mb-1">Recent source-to-page paths</h2>
              <p className="text-muted mb-0">
                See each visitor&apos;s acquisition origin, approximate location, entry page, and latest activity.
              </p>
            </div>
            <div className="visitor-card-actions">
              <span className="visitor-retention-badge"><Database size={15} /> All-time archive</span>
              <ReportButton
                onClick={reports.visitors}
                disabled={!analytics}
                busy={visitorState.exportingCsv}
              />
            </div>
          </div>

          <div className="visitor-table-toolbar">
            <label className="visitor-search-field">
              <Search size={16} />
              <span className="visually-hidden">Search visitor records</span>
              <input
                type="search"
                value={visitorState.search}
                onChange={(event) => {
                  visitorState.setSearch(event.target.value);
                  visitorState.setRowsShown(10);
                }}
                placeholder="Search source, campaign, city, country, or page"
              />
            </label>
            <select
              className="visitor-filter-select"
              value={visitorState.locationFilter}
              onChange={(event) => {
                visitorState.setLocationFilter(event.target.value);
                visitorState.setRowsShown(10);
              }}
              aria-label="Filter visitors by location coverage"
            >
              <option value="all">All locations</option>
              <option value="resolved">City resolved</option>
              <option value="unresolved">City unavailable</option>
            </select>
            {!visitorState.allRecords &&
            visitorState.loadedRecords.length <
              (visitorState.meta.total || summary.totalUniqueVisitors || 0) ? (
              <button
                type="button"
                className="visitor-archive-button"
                onClick={visitorState.loadAll}
                disabled={visitorState.loadingAll}
              >
                {visitorState.loadingAll ? "Loading archive…" : "Browse all retained"}
              </button>
            ) : null}
          </div>

          <p className="visitor-table-caption">
            Showing {visitorState.recentRecords.length.toLocaleString()} of{" "}
            {visitorState.filteredRecords.length.toLocaleString()} matching records.
          </p>
          {visitorState.error ? <div className="alert alert-warning py-2">{visitorState.error}</div> : null}

          {visitorState.recentRecords.length ? (
            <div className="table-responsive visitor-table-scroll analytics-journey-table">
              <table className="table align-middle mb-0 dashboard-table">
                <thead><tr><th>Acquisition</th><th>Location</th><th>Journey</th><th>Activity</th><th>Last seen</th></tr></thead>
                <tbody>
                  {visitorState.recentRecords.map((visitor) => {
                    const acquisition = getAcquisition(visitor);
                    const cityResolved = visitor.city && visitor.city !== "Unknown";
                    return (
                      <tr key={visitor._id || visitor.visitorId}>
                        <td className="analytics-acquisition-cell">
                          <strong>{acquisitionLabel(acquisition.source)}</strong>
                          <span>{acquisition.channel || "Unattributed"}</span>
                          <small>{acquisition.referrerHost || "No external referrer"}</small>
                          {acquisition.campaign ? <em>{acquisition.campaign}</em> : null}
                        </td>
                        <td className="visitor-location-cell">
                          <div className="visitor-location-primary">
                            <MapPinned size={16} />
                            <span><strong>{cityResolved ? visitor.city : "City unavailable"}</strong><small>{[visitor.region, getCountryLabel(visitor.country)].filter((value) => value && value !== "Unknown").join(", ") || "Country unavailable"}</small></span>
                          </div>
                        </td>
                        <td className="analytics-journey-cell">
                          <span><i>Entry</i>{truncate(formatPagePath(acquisition.landingPage || visitor.firstPage), 54)}</span>
                          <span><i>Latest</i>{truncate(formatPagePath(visitor.lastPage), 54)}</span>
                          {acquisition.redirectCount ? <small>{acquisition.redirectCount} redirect{acquisition.redirectCount === 1 ? "" : "s"} before landing</small> : null}
                        </td>
                        <td className="analytics-activity-cell">
                          <strong>{visitor.visitCount || 0} visits</strong>
                          <span>{visitor.pageViewCount || 0} views · {formatDuration(visitor.totalEngagementMs)}</span>
                          <small>{visitor.device?.type || "Unknown device"} · {visitor.device?.browser || "Unknown browser"}</small>
                        </td>
                        <td className="text-nowrap">{formatDateTime(visitor.lastSeenAt)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : <div className="empty-panel">No visitor journeys match these filters.</div>}

          {visitorState.recentRecords.length < visitorState.filteredRecords.length ? (
            <button type="button" className="visitor-show-more-button" onClick={() => visitorState.setRowsShown((current) => current + 25)}>
              Show 25 more
            </button>
          ) : null}
        </div>
      </section>
    </>
  );
};

export default WebsiteAnalyticsDashboard;
